import { z } from 'zod';
import { CONSENT_SOURCES, CONSENT_STATUSES, DATA_SUBJECT_TYPES, NOTICE_TYPES, PRIVACY_PURPOSES, PROCESSING_BASES, } from '../privacy.js';
import { noteText, optionalText, shortText } from './common.js';
/* ---------------- consent ---------------- */
export const consentSubjectSchema = z.object({
    subjectType: z.enum(DATA_SUBJECT_TYPES),
    subjectId: z.string().uuid(),
});
export const recordConsentSchema = consentSubjectSchema.extend({
    purpose: z.enum(PRIVACY_PURPOSES),
    status: z.enum(CONSENT_STATUSES).exclude(['WITHDRAWN']),
    source: z.enum(CONSENT_SOURCES),
    /** The notice version the person was shown. */
    noticeId: z.string().uuid().optional(),
});
export const withdrawConsentSchema = z.object({
    source: z.enum(CONSENT_SOURCES).default('ADMIN'),
});
/**
 * Consent choices captured alongside a guest-facing form (check-in, booking,
 * guest menu): only purposes a person actually ticked or declined. A purpose
 * nobody was asked about is simply absent - never recorded as consent.
 */
export const consentChoicesSchema = z
    .array(z.object({
    purpose: z.enum(PRIVACY_PURPOSES),
    granted: z.boolean(),
}))
    .max(PRIVACY_PURPOSES.length)
    .optional();
/**
 * What a guest-facing form sends about privacy: the notice version the person
 * was shown, and the consent choices they made (if any were offered).
 */
export const privacyCaptureSchema = z
    .object({
    noticeId: z.string().uuid().optional(),
    consents: consentChoicesSchema,
})
    .optional();
/* ---------------- notices ---------------- */
const versionString = z
    .string()
    .trim()
    .min(1)
    .max(32)
    .regex(/^[A-Za-z0-9._-]+$/, 'Use letters, digits, dots and dashes, e.g. 2026-10-06 or 1.2');
export const createNoticeSchema = z.object({
    type: z.enum(NOTICE_TYPES),
    version: versionString,
    title: shortText,
    summary: z.string().trim().min(1).max(2000),
    content: z.string().trim().min(1).max(200_000),
    requiresAcceptance: z.boolean().default(false),
    effectiveAt: z.string().datetime().optional(),
});
export const updateNoticeSchema = createNoticeSchema
    .omit({ type: true, version: true })
    .partial();
/* ---------------- purposes ---------------- */
export const purposeSettingSchema = z.object({
    processingBasis: z.enum(PROCESSING_BASES),
    consentRequired: z.boolean().nullable(),
    notes: noteText,
});
/* ---------------- data principal requests ---------------- */
export const DATA_REQUEST_TYPES = [
    'ACCESS',
    'CORRECTION',
    'ERASURE',
    'CONSENT_WITHDRAWAL',
    'GRIEVANCE',
    'OTHER',
];
export const DATA_REQUEST_STATUSES = [
    'SUBMITTED',
    'IDENTITY_VERIFICATION',
    'UNDER_REVIEW',
    'APPROVED',
    'REJECTED',
    'PROCESSING',
    'COMPLETED',
    'CANCELLED',
];
export const VERIFICATION_STATUSES = ['PENDING', 'VERIFIED', 'FAILED', 'NOT_REQUIRED'];
export const createDataRequestSchema = z.object({
    requestType: z.enum(DATA_REQUEST_TYPES),
    subjectType: z.enum(DATA_SUBJECT_TYPES),
    subjectId: z.string().uuid().optional(),
    subjectLabel: optionalText,
    contact: optionalText,
    channel: optionalText,
    notes: noteText,
    verificationStatus: z.enum(VERIFICATION_STATUSES).optional(),
});
/**
 * A request a guest submits themselves from the hotel's public pages. The
 * hotel verifies who they are before acting on it.
 */
export const publicDataRequestSchema = z.object({
    requestType: z.enum(DATA_REQUEST_TYPES),
    name: shortText,
    contact: z.string().trim().min(5, 'Give an email or phone number to reply to').max(120),
    details: z.string().trim().min(5).max(500),
});
export const updateDataRequestSchema = z.object({
    status: z.enum(DATA_REQUEST_STATUSES).optional(),
    verificationStatus: z.enum(VERIFICATION_STATUSES).optional(),
    assignedToId: z.string().uuid().nullable().optional(),
    subjectId: z.string().uuid().optional(),
    resolution: noteText,
    notes: noteText,
});
/** A guest correction: the ordinary fields. The ID number is separate and needs more. */
export const guestCorrectionSchema = z
    .object({
    name: shortText.optional(),
    phone: z.string().trim().max(20).optional().or(z.literal('')),
    email: z.string().trim().email().optional().or(z.literal('')),
    address: z.string().trim().max(240).optional().or(z.literal('')),
    gstin: z.string().trim().max(15).optional().or(z.literal('')),
    reason: z.string().trim().min(3).max(500),
})
    .refine((input) => Object.keys(input).some((key) => key !== 'reason'), 'Change at least one field');
export const guestIdentityCorrectionSchema = z.object({
    idType: z.enum(['AADHAAR', 'PASSPORT', 'DRIVING_LICENCE', 'VOTER_ID']).optional(),
    idNumber: z.string().trim().max(30).optional().or(z.literal('')),
    reason: z.string().trim().min(3).max(500),
});
export const erasureSchema = z.object({
    reason: z.string().trim().min(3).max(500),
    /** The data principal request this erasure fulfils, if any. */
    requestId: z.string().uuid().optional(),
});
/* ---------------- self-service ---------------- */
export const updateOwnProfileSchema = z
    .object({
    name: shortText.optional(),
    phone: z.string().trim().max(20).optional().or(z.literal('')),
})
    .refine((input) => input.name !== undefined || input.phone !== undefined, 'Nothing to change');
/* ---------------- retention ---------------- */
export const RETENTION_ENTITIES = [
    'EMAIL_OTP',
    'PASSWORD_RESET_TOKEN',
    'DEVICE_PAIRING_CODE',
    'SESSION',
    'NOTIFICATION',
    'DEVICE_ERROR',
    'DEVICE_UPDATE_ATTEMPT',
    'SYNC_OPERATION',
    'BILLING_WEBHOOK_EVENT',
    'COUPON_REDEMPTION_METADATA',
    'CAPTCHA_CHALLENGE',
    'ERROR_LOG',
    'AUDIT_SECURITY',
    'AUDIT_PRIVACY',
    'AUDIT_BUSINESS',
    'GUEST',
    'ROOM_BOOKING',
    'RESERVATION',
    'ORDER_CUSTOMER',
    'EMPLOYEE',
    'DATA_REQUEST_CONTACT',
    'CLOSED_ACCOUNT',
];
export const RETENTION_ACTIONS = ['DELETE', 'ANONYMIZE', 'PURGE_SENSITIVE_FIELDS'];
export const retentionPolicySchema = z.object({
    entity: z.enum(RETENTION_ENTITIES),
    /** Null for the platform default; a tenant id for an override. */
    tenantId: z.string().uuid().nullable().default(null),
    retentionDays: z.number().int().min(1).max(36500),
    action: z.enum(RETENTION_ACTIONS),
    enabled: z.boolean(),
    note: noteText,
    /** Why the period applies - the law or business need. "To confirm" until settled. */
    legalBasis: z.string().trim().max(500).optional().or(z.literal('')),
});
/* ---------------- disclosures ---------------- */
export const createDisclosureSchema = z.object({
    authority: shortText,
    requestType: shortText,
    receivedAt: z.string().datetime(),
    requestedData: z.string().trim().min(3).max(5000),
    tenantId: z.string().uuid().optional(),
    propertyId: z.string().uuid().optional(),
    legalReference: optionalText,
});
export const DISCLOSURE_STATUSES = [
    'RECEIVED',
    'UNDER_REVIEW',
    'APPROVED',
    'REJECTED',
    'PARTIALLY_APPROVED',
    'DISCLOSED',
    'CLOSED',
];
export const disclosureReviewSchema = z.object({
    notes: z.string().trim().min(3).max(5000),
    /** The order, notice or provision the request relies on, once known. */
    legalReference: optionalText,
});
export const disclosureDecisionSchema = z.object({
    decision: z.enum(['APPROVED', 'PARTIALLY_APPROVED', 'REJECTED']),
    notes: z.string().trim().min(3).max(5000),
});
export const disclosureRecordSchema = z.object({
    disclosedTo: shortText,
    disclosureSummary: z.string().trim().min(3).max(5000),
});
/* ---------------- incidents ---------------- */
export const INCIDENT_TYPES = [
    'DATA_BREACH',
    'UNAUTHORIZED_ACCESS',
    'CREDENTIAL_EXPOSURE',
    'DEVICE_LOSS',
    'BACKUP_EXPOSURE',
    'SUSPICIOUS_EXPORT',
    'OTHER',
];
export const INCIDENT_SEVERITIES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
export const INCIDENT_STATUSES = ['OPEN', 'INVESTIGATING', 'CONTAINED', 'RESOLVED', 'CLOSED'];
export const createIncidentSchema = z.object({
    type: z.enum(INCIDENT_TYPES),
    severity: z.enum(INCIDENT_SEVERITIES),
    title: shortText,
    description: z.string().trim().min(3).max(10_000),
    detectedAt: z.string().datetime(),
    tenantId: z.string().uuid().optional(),
    propertyId: z.string().uuid().optional(),
    affectedSystems: z.array(z.string().trim().min(1).max(80)).max(30).default([]),
    affectedDataCategories: z.array(z.string().trim().min(1).max(80)).max(30).default([]),
    affectedSubjectsEstimate: z.number().int().min(0).optional(),
});
export const updateIncidentSchema = z.object({
    status: z.enum(INCIDENT_STATUSES).optional(),
    severity: z.enum(INCIDENT_SEVERITIES).optional(),
    assignedToId: z.string().uuid().nullable().optional(),
    containmentAction: z.string().trim().max(5000).optional(),
    resolution: z.string().trim().max(5000).optional(),
    affectedSystems: z.array(z.string().trim().min(1).max(80)).max(30).optional(),
    affectedDataCategories: z.array(z.string().trim().min(1).max(80)).max(30).optional(),
    affectedSubjectsEstimate: z.number().int().min(0).nullable().optional(),
});
/* ---------------- processors ---------------- */
export const PROCESSOR_CONTRACT_STATUSES = [
    'UNKNOWN',
    'NOT_STARTED',
    'IN_REVIEW',
    'SIGNED',
    'NOT_REQUIRED',
];
export const processorSchema = z.object({
    provider: shortText,
    purpose: z.string().trim().min(3).max(500),
    dataCategories: z.array(z.string().trim().min(1).max(60)).max(30),
    dataTransferred: z.string().trim().min(3).max(2000),
    environment: z.string().trim().min(1).max(40).default('ALL'),
    /** UNKNOWN until established from the contract or the provider - never guessed. */
    region: z.string().trim().min(1).max(80).default('UNKNOWN'),
    contractStatus: z.enum(PROCESSOR_CONTRACT_STATUSES).default('UNKNOWN'),
    active: z.boolean().default(true),
    notes: noteText,
});
/* ---------------- support access ---------------- */
export const openSupportAccessSchema = z.object({
    tenantId: z.string().uuid(),
    propertyId: z.string().uuid().optional(),
    reason: z.string().trim().min(10, 'Say why - at least a sentence').max(1000),
    ticketReference: z.string().trim().min(2).max(80),
    /** How long the window stays open. Kept short: re-open it if the work runs on. */
    minutes: z.number().int().min(5).max(480).default(60),
});
/* ---------------- tenant offboarding ---------------- */
export const requestOffboardingSchema = z.object({
    reason: noteText,
    /**
     * The code emailed to the owner's registered address (POST
     * /account/offboarding/code): a signed-in session alone cannot close an
     * account.
     */
    code: z.string().trim().regex(/^\d{6}$/, 'Enter the 6-digit code from the email'),
});
export const startOffboardingSchema = z.object({
    /** Required to start before the grace period ends - the account's written request, say. */
    earlyReason: z.string().trim().min(10).max(1000).optional(),
});
//# sourceMappingURL=privacy.js.map